/**
 * Unified PDP inventory gate — one getProducts batch when blocked + notify share the
 * same selection IDs (e.g. Complex PDP change set).
 */

import {useMemo} from 'react';

import {useCommittedCoverVariantProduct} from '@amblique/king-living/app/hooks/use-committed-cover-variant-product';
import {usePdpShopperInventoryIds} from '@amblique/king-living/app/hooks/use-pdp-shopper-inventory-ids';
import {
  buildPdpInventoryProductIds,
  usePdpSelectionInventoryProducts,
} from '@amblique/king-living/app/hooks/use-pdp-selection-inventory-products';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';
import {getInventory, INVENTORY_STATUS, shouldShowNotifyMeForProduct} from '@amblique/king-living/app/utils/inventory-utils';

function isProductOutOfStockForWarehouse(product, variant, shopperInventoryIds) {
  return getInventory(product, variant, shopperInventoryIds).status === INVENTORY_STATUS.OUT_OF_STOCK;
}

/**
 * @param {Object} options
 * @param {Object|null|undefined} options.product
 * @param {Object|null|undefined} options.variant
 * @param {Object|null|undefined} options.blockedSelections - Live selections for ATC block (draft or committed)
 * @param {Object|null|undefined} options.committedSelection
 * @param {boolean} [options.blockedEnabled=true]
 * @param {boolean} [options.notifyEnabled=true]
 * @param {boolean} [options.isSimplePdpFlow=false]
 * @param {boolean} [options.isSimpleMasterWithCovers=false]
 * @returns {{
 *   isAddToCartBlocked: boolean,
 *   isInventoryPending: boolean,
 *   notifyMeMode: boolean,
 *   primaryVariantProduct: Object|null,
 * }}
 */
export function usePdpInventoryGate({
  product,
  variant,
  blockedSelections,
  committedSelection,
  blockedEnabled = true,
  notifyEnabled = true,
  isSimplePdpFlow = false,
  isSimpleMasterWithCovers = false,
}) {
  const {shopperInventoryIds, isInventoryIdsResolving} = usePdpShopperInventoryIds();
  const notifySelections = committedSelection?.selections;
  const coverVariant = useCommittedCoverVariantProduct(product);

  const blockedProductIds = useMemo(
    () => buildPdpInventoryProductIds(blockedSelections, {product, variant}),
    [blockedSelections, product, variant]
  );

  const notifyProductIds = useMemo(
    () => (notifyEnabled && notifySelections ?
      buildPdpInventoryProductIds(notifySelections, {product, variant}) :
      []),
    [notifyEnabled, notifySelections, product, variant]
  );

  const primaryProductId = useMemo(() => {
    const coverId = blockedSelections?.cover?.productId ?? blockedSelections?.cover?.variantId;

    return coverId ?? variant?.productId ?? product?.id ?? product?.productId ?? null;
  }, [blockedSelections, variant, product]);

  const sharedProductIds = useMemo(() => {
    if (blockedProductIds.length === 0 || notifyProductIds.length === 0) {
      return [];
    }

    return blockedProductIds.join(',') === notifyProductIds.join(',') ? blockedProductIds : [];
  }, [blockedProductIds, notifyProductIds]);

  const needsSharedFetch = sharedProductIds.length > 0;
  const needsBlockedOnlyFetch =
    blockedEnabled && blockedProductIds.length > 0 && !needsSharedFetch;
  const needsNotifyOnlyFetch =
    notifyEnabled && notifyProductIds.length > 0 && !needsSharedFetch;

  const sharedInventory = usePdpSelectionInventoryProducts({
    enabled: needsSharedFetch && (blockedEnabled || notifyEnabled),
    productIds: sharedProductIds,
  });

  const blockedOnlyInventory = usePdpSelectionInventoryProducts({
    enabled: needsBlockedOnlyFetch,
    productIds: blockedProductIds,
  });

  const notifyOnlyInventory = usePdpSelectionInventoryProducts({
    enabled: needsNotifyOnlyFetch,
    productIds: notifyProductIds,
  });

  const blockedInventory = needsSharedFetch ? sharedInventory : blockedOnlyInventory;
  const notifyInventory = needsSharedFetch ? sharedInventory : notifyOnlyInventory;

  const isInventoryPending =
    isInventoryIdsResolving ||
    (blockedEnabled && blockedInventory.isPending) ||
    (notifyEnabled && notifyInventory.isPending);

  const primaryVariantProduct = useMemo(
    () => (primaryProductId ? blockedInventory.productsById[primaryProductId] ?? null : null),
    [primaryProductId, blockedInventory.productsById]
  );

  const isAddToCartBlocked = useMemo(() => {
    if (!blockedEnabled || !product) {
      return false;
    }

    if (isInventoryIdsResolving) {
      return true;
    }

    const coverId = blockedSelections?.cover?.productId ?? blockedSelections?.cover?.variantId;
    const coverVariantProduct = coverId ? getSelectedVariantProduct(product, coverId) : null;
    const primaryVariant = coverVariantProduct ?? variant ?? undefined;

    if (isProductOutOfStockForWarehouse(product, primaryVariant, shopperInventoryIds)) {
      return true;
    }

    if (blockedProductIds.length > 0 && blockedInventory.isPending) {
      return true;
    }

    if (blockedProductIds.length > 0) {
      const {productsById} = blockedInventory;
      const allLoaded = blockedProductIds.every((id) => productsById[id]);

      if (!allLoaded) {
        return true;
      }

      const hasBlockedSelection = blockedProductIds.some((id) =>
        isProductOutOfStockForWarehouse(productsById[id], undefined, shopperInventoryIds)
      );

      if (hasBlockedSelection) {
        return true;
      }
    }

    return false;
  }, [
    blockedEnabled,
    product,
    blockedSelections,
    variant,
    shopperInventoryIds,
    isInventoryIdsResolving,
    blockedProductIds,
    blockedInventory,
  ]);

  const notifyMeMode = useMemo(() => {
    if (!notifyEnabled || !notifySelections || notifyInventory.isPending) {
      return false;
    }

    const coverId = notifySelections.cover?.productId ?? notifySelections.cover?.variantId;
    const {productsById} = notifyInventory;

    if (!isSimplePdpFlow && coverId) {
      const coverProduct = productsById[coverId];
      const coverSku = coverProduct ?? coverVariant;

      if (coverSku && shouldShowNotifyMeForProduct(coverSku, null, shopperInventoryIds)) {
        return true;
      }
    } else if (isSimplePdpFlow && !isSimpleMasterWithCovers) {
      if (shouldShowNotifyMeForProduct(product, null, shopperInventoryIds)) {
        return true;
      }
    }

    if (notifyProductIds.length === 0) {
      return false;
    }

    return notifyProductIds.some((id) => {
      const childProduct = productsById[id];

      if (!childProduct) {
        return true;
      }

      return shouldShowNotifyMeForProduct(childProduct, null, shopperInventoryIds);
    });
  }, [
    notifyEnabled,
    notifySelections,
    notifyInventory,
    isSimplePdpFlow,
    isSimpleMasterWithCovers,
    product,
    coverVariant,
    shopperInventoryIds,
    notifyProductIds,
  ]);

  return {
    isAddToCartBlocked,
    isInventoryPending,
    notifyMeMode,
    primaryVariantProduct,
  };
}

export default usePdpInventoryGate;
